import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { GST_RATES, formatINR } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Package, Trash2, Pencil, Search, Upload, Sparkles, X, CheckSquare } from "lucide-react";
import { toast } from "sonner";
import HsnCombobox from "@/components/HsnCombobox";

export default function ItemsServicesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isUnregistered = user?.gst_status === "unregistered";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [form, setForm] = useState({
    name: "", type: "service", hsn_sac: "", unit: "NOS", rate: "", gst_rate: isUnregistered ? "0" : "18", description: "",
  });
  const [saving, setSaving] = useState(false);
  const [hsnError, setHsnError] = useState("");
  const [uomUnits, setUomUnits] = useState([]);
  const [addUomOpen, setAddUomOpen] = useState(false);
  const [newUom, setNewUom] = useState({ code: "", name: "" });
  const [savingUom, setSavingUom] = useState(false);

  // Smart Defaults state
  const [sectorDefaults, setSectorDefaults] = useState([]);
  const [sectorName, setSectorName] = useState("");
  const [selectedDefaults, setSelectedDefaults] = useState(new Set());
  const [showDefaults, setShowDefaults] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [defaultsDismissed, setDefaultsDismissed] = useState(false);

  const fetchUoms = useCallback(() => {
    api.get("/uom-units").then((r) => setUomUnits(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/items").then((r) => setItems(r.data)).finally(() => setLoading(false));
    fetchUoms();
    // Fetch sector defaults
    api.get("/items/sector-defaults").then((r) => {
      if (r.data.items && r.data.items.length > 0) {
        setSectorDefaults(r.data.items);
        setSectorName(r.data.sector);
        setSelectedDefaults(new Set(r.data.items.map((_, i) => i)));
        setShowDefaults(true);
      }
    }).catch(() => {});
  }, [fetchUoms]);

  const toggleDefault = (idx) => {
    setSelectedDefaults((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  const toggleAllDefaults = () => {
    if (selectedDefaults.size === sectorDefaults.length) {
      setSelectedDefaults(new Set());
    } else {
      setSelectedDefaults(new Set(sectorDefaults.map((_, i) => i)));
    }
  };

  const seedDefaults = async () => {
    if (selectedDefaults.size === 0) { toast.error("Select at least one item"); return; }
    setSeeding(true);
    try {
      const res = await api.post("/items/seed-from-sector", { indices: Array.from(selectedDefaults) });
      toast.success(`Added ${res.data.added} items${res.data.skipped_duplicates > 0 ? ` (${res.data.skipped_duplicates} duplicates skipped)` : ""}`);
      // Refresh items list
      const refreshed = await api.get("/items");
      setItems(refreshed.data);
      setDefaultsDismissed(true);
    } catch {
      toast.error("Failed to seed items");
    } finally {
      setSeeding(false);
    }
  };

  const saveCustomUom = async () => {
    const code = newUom.code.trim().toUpperCase();
    const name = newUom.name.trim();
    if (!code || !name) { toast.error("Code and name are required"); return; }
    setSavingUom(true);
    try {
      const res = await api.post("/uom-units", { code, name });
      setUomUnits((prev) => [...prev, res.data]);
      setForm((prev) => ({ ...prev, unit: res.data.code }));
      setAddUomOpen(false);
      setNewUom({ code: "", name: "" });
      toast.success(`UOM '${res.data.code}' added!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add UOM");
    } finally {
      setSavingUom(false);
    }
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    if (field === "hsn_sac") {
      const cleaned = value.replace(/\D/g, "");
      if (cleaned.length > 0 && cleaned.length < 6) {
        setHsnError("HSN/SAC code must be at least 6 digits");
      } else {
        setHsnError("");
      }
    }
  };

  const openCreate = () => {
    setEditId(null);
    setHsnError("");
    setForm({ name: "", type: "service", hsn_sac: "", unit: "NOS", rate: "", gst_rate: isUnregistered ? "0" : "18", description: "" });
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditId(item.item_id);
    setHsnError("");
    setForm({
      name: item.name, type: item.type, hsn_sac: item.hsn_sac || "",
      unit: item.unit || "NOS", rate: String(item.rate || ""),
      gst_rate: String(item.gst_rate || (isUnregistered ? "0" : "18")), description: item.description || "",
    });
    setDialogOpen(true);
  };

  const saveItem = async () => {
    if (!form.name) { toast.error("Name is required"); return; }
    const cleanedHsn = form.hsn_sac.replace(/\D/g, "");
    if (cleanedHsn.length > 0 && cleanedHsn.length < 6) {
      setHsnError("HSN/SAC code must be at least 6 digits");
      toast.error("HSN/SAC code must be at least 6 digits");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, rate: Number(form.rate) || 0, gst_rate: Number(form.gst_rate) || (isUnregistered ? 0 : 18) };
      if (editId) {
        const response = await api.put(`/items/${editId}`, payload);
        setItems(items.map((i) => (i.item_id === editId ? response.data : i)));
        toast.success("Item updated!");
      } else {
        const response = await api.post("/items", payload);
        setItems([response.data, ...items]);
        toast.success("Item added!");
      }
      setDialogOpen(false);
    } catch {
      toast.error("Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    try {
      await api.delete(`/items/${id}`);
      setItems(items.filter((i) => i.item_id !== id));
      toast.success("Item deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const filtered = items.filter((i) => {
    const matchesSearch = !search || i.name.toLowerCase().includes(search.toLowerCase()) || (i.hsn_sac && i.hsn_sac.includes(search));
    const matchesType = typeFilter === "all" || i.type === typeFilter;
    return matchesSearch && matchesType;
  });

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="items-services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Items & Services</h1>
          <p className="text-sm text-[#64748b] mt-1">{items.length} items in catalog</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/items/import")} data-testid="import-items-btn" className="border-[#e2e8f0] rounded-xl">
            <Upload className="h-4 w-4 mr-2" /> Import
          </Button>
          <Button onClick={openCreate} data-testid="add-item-btn" className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
            <Plus className="h-4 w-4 mr-2" /> Add Item
          </Button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#94a3b8]" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or HSN/SAC..."
            data-testid="items-search-input"
            className="pl-9 h-10 border-[#e2e8f0]"
          />
        </div>
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList data-testid="items-type-filter">
            <TabsTrigger value="all">All</TabsTrigger>
            <TabsTrigger value="goods">Goods</TabsTrigger>
            <TabsTrigger value="service">Services</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">{editId ? "Edit Item" : "Add Item / Service"}</DialogTitle>
            <DialogDescription className="text-sm text-[#64748b]">Define your item with HSN/SAC and unit of measurement</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">Name *</Label>
                <Input value={form.name} onChange={(e) => handleChange("name", e.target.value)} data-testid="item-name-input" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-sm font-medium">Type *</Label>
                <Select value={form.type} onValueChange={(v) => handleChange("type", v)}>
                  <SelectTrigger data-testid="item-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="goods">Goods (HSN)</SelectItem>
                    <SelectItem value="service">Service (SAC)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium">{form.type === "goods" ? "HSN Code" : "SAC Code"}</Label>
                <div className="mt-1.5">
                  <HsnCombobox
                    value={form.hsn_sac}
                    itemType={form.type}
                    onChange={(code) => {
                      handleChange("hsn_sac", code);
                    }}
                    onSelect={(entry) => {
                      if (entry) {
                        setForm((prev) => ({
                          ...prev,
                          hsn_sac: entry.code,
                          gst_rate: String(entry.gst_rate),
                        }));
                        setHsnError("");
                      }
                    }}
                  />
                </div>
                {hsnError && <p className="text-xs text-red-500 mt-1" data-testid="hsn-error-message">{hsnError}</p>}
              </div>
              <div>
                <Label className="text-sm font-medium">{form.type === "goods" ? "Unit (Weight/Qty)" : "Unit (Hours/Sessions)"}</Label>
                <Select value={form.unit} onValueChange={(v) => {
                  if (v === "__add_custom__") { setAddUomOpen(true); return; }
                  handleChange("unit", v);
                }}>
                  <SelectTrigger data-testid="item-unit-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {uomUnits.map((u) => (
                      <SelectItem key={u.code} value={u.code}>{u.code} - {u.name}</SelectItem>
                    ))}
                    <SelectItem value="__add_custom__" data-testid="add-custom-uom-option">
                      <span className="flex items-center gap-1.5 text-[#4338ca]"><Plus className="h-3 w-3" /> Add Custom UOM</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className={isUnregistered ? "" : "grid grid-cols-2 gap-4"}>
              <div>
                <Label className="text-sm font-medium">Default Rate (INR)</Label>
                <Input type="number" value={form.rate} onChange={(e) => handleChange("rate", e.target.value)} placeholder="0.00" data-testid="item-rate-input" className="mt-1.5" />
              </div>
              {!isUnregistered && (
              <div>
                <Label className="text-sm font-medium">GST Rate</Label>
                <Select value={String(form.gst_rate)} onValueChange={(v) => handleChange("gst_rate", v)}>
                  <SelectTrigger data-testid="item-gst-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {GST_RATES.map((r) => (
                      <SelectItem key={r} value={String(r)}>{r}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>
            <div>
              <Label className="text-sm font-medium">Description</Label>
              <Input value={form.description} onChange={(e) => handleChange("description", e.target.value)} className="mt-1.5" />
            </div>
            <Button onClick={saveItem} disabled={saving} data-testid="save-item-btn" className="w-full bg-[#4338ca] hover:bg-[#3730a3] rounded-xl">
              {saving ? "Saving..." : editId ? "Update Item" : "Add Item"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Smart Defaults by Sector Banner */}
      {showDefaults && !defaultsDismissed && sectorDefaults.length > 0 && (
        <Card className="border-[#4338ca]/20 bg-gradient-to-r from-[#4338ca]/[0.04] to-[#6366f1]/[0.04] shadow-sm" data-testid="smart-defaults-banner">
          <CardContent className="p-5">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2.5">
                <div className="h-9 w-9 rounded-lg bg-[#4338ca]/10 flex items-center justify-center">
                  <Sparkles className="h-4.5 w-4.5 text-[#4338ca]" />
                </div>
                <div>
                  <h3 className="font-outfit text-sm font-semibold text-[#0f172a]">
                    Smart Defaults for {sectorName}
                  </h3>
                  <p className="text-xs text-[#64748b] mt-0.5">
                    Pre-configured items with correct HSN/SAC codes and GST rates for your sector
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setDefaultsDismissed(true)} data-testid="dismiss-defaults-btn" className="text-[#94a3b8] hover:text-[#64748b] -mt-1 -mr-2 h-7 w-7 p-0">
                <X className="h-4 w-4" />
              </Button>
            </div>
            <div className="bg-white rounded-lg border border-[#e2e8f0] divide-y divide-[#f1f5f9] max-h-[280px] overflow-y-auto">
              <div className="flex items-center px-3 py-2 bg-[#f8fafc] sticky top-0 z-10 border-b border-[#e2e8f0]">
                <button onClick={toggleAllDefaults} className="flex items-center gap-2 text-xs font-medium text-[#4338ca] hover:text-[#3730a3] transition-colors" data-testid="select-all-defaults">
                  <CheckSquare className="h-3.5 w-3.5" />
                  {selectedDefaults.size === sectorDefaults.length ? "Deselect All" : "Select All"}
                </button>
                <span className="ml-auto text-xs text-[#94a3b8]">{selectedDefaults.size} of {sectorDefaults.length} selected</span>
              </div>
              {sectorDefaults.map((item, idx) => (
                <label
                  key={idx}
                  className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[#f8fafc] transition-colors"
                  data-testid={`default-item-${idx}`}
                >
                  <Checkbox
                    checked={selectedDefaults.has(idx)}
                    onCheckedChange={() => toggleDefault(idx)}
                    data-testid={`default-checkbox-${idx}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-[#0f172a] truncate">{item.name}</span>
                      <Badge variant="secondary" className={`text-[10px] px-1.5 py-0 shrink-0 ${item.type === "goods" ? "bg-[#f59e0b]/10 text-[#f59e0b]" : "bg-[#3b82f6]/10 text-[#3b82f6]"}`}>
                        {item.type}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-[#94a3b8]">
                      {item.hsn_sac && <span className="font-mono">{item.type === "goods" ? "HSN" : "SAC"}: {item.hsn_sac}</span>}
                      <span>GST: {item.gst_rate}%</span>
                      {item.rate > 0 && <span>{formatINR(item.rate)}</span>}
                      <span>{item.unit}</span>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex items-center justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setDefaultsDismissed(true)} className="rounded-lg border-[#e2e8f0] text-sm" data-testid="skip-defaults-btn">
                Skip
              </Button>
              <Button
                size="sm"
                onClick={seedDefaults}
                disabled={seeding || selectedDefaults.size === 0}
                className="bg-[#4338ca] hover:bg-[#3730a3] rounded-lg text-sm"
                data-testid="add-defaults-btn"
              >
                {seeding ? "Adding..." : `Add ${selectedDefaults.size} Item${selectedDefaults.size !== 1 ? "s" : ""} to Catalog`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-[#64748b]">Loading...</div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <Package className="h-12 w-12 text-[#e2e8f0] mx-auto mb-3" />
              <p className="text-sm text-[#64748b]">{search ? "No items match your search." : "No items yet. Add your first item or service."}</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#e2e8f0] bg-[#f8fafc]">
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Name</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">Type</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">HSN/SAC</th>
                  <th className="text-left py-3 px-4 text-[#64748b] font-medium">UOM</th>
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Rate</th>
                  {!isUnregistered && <th className="text-center py-3 px-4 text-[#64748b] font-medium">GST</th>}
                  <th className="text-right py-3 px-4 text-[#64748b] font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.item_id} className="border-b border-[#f1f5f9] last:border-0 hover:bg-[#f8fafc]" data-testid={`item-row-${item.item_id}`}>
                    <td className="py-3 px-4">
                      <p className="text-[#0f172a] font-medium">{item.name}</p>
                      {item.description && <p className="text-xs text-[#94a3b8] mt-0.5">{item.description}</p>}
                    </td>
                    <td className="py-3 px-4">
                      <Badge className={item.type === "goods" ? "bg-[#f59e0b]/10 text-[#f59e0b]" : "bg-[#3b82f6]/10 text-[#3b82f6]"}>
                        {item.type}
                      </Badge>
                    </td>
                    <td className="py-3 px-4 font-mono text-xs text-[#64748b]">{item.hsn_sac || "-"}</td>
                    <td className="py-3 px-4 text-[#64748b]">{item.unit}</td>
                    <td className="py-3 px-4 text-right font-mono font-semibold text-[#0f172a]">{formatINR(item.rate)}</td>
                    {!isUnregistered && <td className="py-3 px-4 text-center text-[#64748b]">{item.gst_rate}%</td>}
                    <td className="py-3 px-4 text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(item)} data-testid={`edit-item-${item.item_id}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => deleteItem(item.item_id)} className="text-red-500" data-testid={`delete-item-${item.item_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Add Custom UOM Dialog */}
      <Dialog open={addUomOpen} onOpenChange={setAddUomOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl">Add Custom UOM</DialogTitle>
            <DialogDescription className="text-sm text-[#64748b]">Define a new unit of measurement</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-sm font-medium">Code *</Label>
              <Input
                value={newUom.code}
                onChange={(e) => setNewUom({ ...newUom, code: e.target.value.toUpperCase() })}
                placeholder="e.g., SFT"
                maxLength={10}
                data-testid="custom-uom-code-input"
                className="mt-1.5 font-mono uppercase"
              />
              <p className="text-xs text-[#94a3b8] mt-1">Max 10 characters, e.g., SFT, RFT, DOZ</p>
            </div>
            <div>
              <Label className="text-sm font-medium">Name *</Label>
              <Input
                value={newUom.name}
                onChange={(e) => setNewUom({ ...newUom, name: e.target.value })}
                placeholder="e.g., Square Feet"
                data-testid="custom-uom-name-input"
                className="mt-1.5"
              />
            </div>
            <Button
              onClick={saveCustomUom}
              disabled={savingUom}
              data-testid="save-custom-uom-btn"
              className="w-full bg-[#4338ca] hover:bg-[#3730a3] rounded-xl"
            >
              {savingUom ? "Saving..." : "Add UOM"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
